import React from 'react';
import styles from './ProgramWeek.module.scss';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { IconButton } from '@mui/material';
import { CalendarWeek } from './CalendarWeek';
import { Day, locales } from '../../constants';
import { buildSlotsForWeek } from '../../utils';

import type { AssignedProgram } from '../../types';
import type { DraggedWorkoutLocation } from './CalendarWeek';

const texts = locales.en.components.programs.programWeek;

interface ProgramWeekProps {
  weekIndex: number;
  setWeekIndex: (index: number) => void;
  program: AssignedProgram;
  onAddWorkout: (weekIndex: number, day: Day) => void;
  onEditWorkout: (weekIndex: number, day: Day) => void;
  onRemoveProgramItem: (weekIndex: number, day: Day) => void;
  onDuplicateWeek: (weekIndex: number) => void;
  onDeleteWeek: (weekIndex: number) => void;
  weeksDates: Date[][] | null;
  onMoveWorkout?: (
    fromWeekIndex: number,
    fromDay: Day,
    toWeekIndex: number,
    toDay: Day,
  ) => void;
  draggingWorkout?: DraggedWorkoutLocation | null;
  setDraggingWorkout?: (value: DraggedWorkoutLocation | null) => void;
}

export const ProgramWeek: React.FC<ProgramWeekProps> = ({
  weekIndex,
  setWeekIndex,
  program,
  onAddWorkout,
  onEditWorkout,
  onRemoveProgramItem,
  onDuplicateWeek,
  onDeleteWeek,
  weeksDates,
  onMoveWorkout,
  draggingWorkout,
  setDraggingWorkout,
}) => {
  const totalWeeks =
    weeksDates && weeksDates.length > 0
      ? weeksDates.length
      : program.programItems.length;
  const lastIndex = Math.max(0, totalWeeks - 1);
  const hasPrev = weekIndex > 0;
  const hasNext = weekIndex < lastIndex;

  const getHeaderText = (): string => {
    if (weeksDates && weeksDates.length > 0 && weekIndex < weeksDates.length) {
      const weekDates = weeksDates[weekIndex];
      const fromDate = weekDates[0];
      const toDate = weekDates[6];
      const fromMonthShort = fromDate.toLocaleString('default', {
        month: 'short',
      });
      const toMonthShort = toDate.toLocaleString('default', { month: 'short' });
      return `${fromMonthShort} ${fromDate.getDate()} - ${toMonthShort} ${toDate.getDate()}, ${toDate.getFullYear()}`;
    }
    return `${texts.week} ${weekIndex + 1}`;
  };

  const goToPreviousWeek = (): void => {
    setWeekIndex(Math.max(0, weekIndex - 1));
  };

  const goToNextWeek = (): void => {
    const totalWeeks =
      weeksDates && weeksDates.length > 0
        ? weeksDates.length
        : program.programItems.length;
    const lastIndex = Math.max(0, totalWeeks - 1);
    setWeekIndex(Math.min(lastIndex, weekIndex + 1));
  };

  const renderHeader = () => {
    return (
      <div
        className={styles.header}
        style={{ width: weeksDates ? '300px' : '200px' }}
      >
        <IconButton
          className={styles.button}
          onClick={goToPreviousWeek}
          disabled={!hasPrev}
          aria-label="Previous week"
        >
          <ChevronLeftIcon color="primary" />
        </IconButton>
        <div className={styles.text}>{getHeaderText()}</div>
        <IconButton
          className={styles.button}
          onClick={goToNextWeek}
          disabled={!hasNext}
          aria-label="Next week"
        >
          <ChevronRightIcon color="primary" />
        </IconButton>
      </div>
    );
  };

  const renderWeek = () => {
    return (
      <div className={styles.weekWrapper} id={`week-${weekIndex}`}>
        <div className={styles.weekContainer}>
          <CalendarWeek
            weekIndex={weekIndex}
            days={buildSlotsForWeek(weekIndex, program, weeksDates)}
            onAddWorkout={(w, d) => onAddWorkout(w, d)}
            onEditWorkout={(w, d) => onEditWorkout(w, d)}
            onRemoveProgramItem={(w, d) => onRemoveProgramItem(w, d)}
            onDuplicateWeek={(w) => onDuplicateWeek(w)}
            onDeleteWeek={(w) => onDeleteWeek(w)}
            onMoveWorkout={onMoveWorkout}
            draggingWorkout={draggingWorkout}
            setDraggingWorkout={setDraggingWorkout}
          />
        </div>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      {renderHeader()}
      {renderWeek()}
    </div>
  );
};
